import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { XIcon, CheckCircleIcon, ChevronUpIcon, ChevronDownIcon, UploadIcon, AlertTriangleIcon, EyeIcon, Trash2Icon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { Dispute, DisputeStatus, DisputeStage, DisputeReason } from '../../../shared/types/Dispute';
import { getEvidenceRecommendation } from '../utils/evidenceRecommendations';
import { generateDisputes } from '../utils/disputes';
interface UploadedFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: Date;
}
export function ChallengeDispute() {
  const {
    t
  } = useTranslation('disputes');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [challengeStep, setChallengeStep] = useState<'reason' | 'evidence' | 'order' | 'customer' | 'other' | 'review' | 'submitted'>('reason');
  const [selectedChallengeReason, setSelectedChallengeReason] = useState('purchasing_history');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [noEvidenceChecked, setNoEvidenceChecked] = useState(false);
  const [noOrderEvidenceChecked, setNoOrderEvidenceChecked] = useState(false);
  const [noCustomerEvidenceChecked, setNoCustomerEvidenceChecked] = useState(false);
  const [noOtherEvidenceChecked, setNoOtherEvidenceChecked] = useState(false);
  const [showNoEvidenceWarning, setShowNoEvidenceWarning] = useState(false);
  const [reviewConfirmChecked, setReviewConfirmChecked] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    challengeDetails: true,
    transactionDetails: true,
    otherEvidence: true
  });
  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [orderUploadedFiles, setOrderUploadedFiles] = useState<UploadedFile[]>([]);
  const [customerUploadedFiles, setCustomerUploadedFiles] = useState<UploadedFile[]>([]);
  const [otherUploadedFiles, setOtherUploadedFiles] = useState<UploadedFile[]>([]);
  // Use centralized dispute generation
  const allDisputes = useMemo(() => generateDisputes(), []);
  const dispute = useMemo(() => {
    console.log('Looking for dispute ID:', id);
    console.log('Total disputes generated:', allDisputes.length);
    console.log('First 10 dispute IDs:', allDisputes.slice(0, 10).map(d => d.id));
    const found = allDisputes.find(d => d.id === id);
    if (!found) {
      console.log('Dispute not found, using fallback (dispute 1)');
      return allDisputes[0];
    }
    console.log('Found dispute:', found.id, 'Card Brand:', found.cardBrand);
    return found;
  }, [id, allDisputes]);
  // Get evidence recommendations based on card brand and reason code
  const evidenceRecs = useMemo(() => {
    console.group('🔍 ChallengeDispute - Evidence Recommendation Lookup');
    console.log('Dispute ID:', dispute.id);
    console.log('Card Brand:', dispute.cardBrand);
    console.log('Network Reason Code:', dispute.networkReasonCode);
    const result = getEvidenceRecommendation(dispute.cardBrand, dispute.networkReasonCode);
    console.log('Lookup Result:', {
      title: result.title,
      description: result.description,
      recommendationsCount: result.recommendations.length,
      recommendations: result.recommendations.map(r => r.title)
    });
    console.groupEnd();
    return result;
  }, [dispute.cardBrand, dispute.networkReasonCode]);
  const getFileFormatRequirements = () => {
    const cardBrand = dispute.cardBrand.toLowerCase();
    // Debug log to verify card brand
    console.log('Dispute ID:', dispute.id, 'Card Brand:', dispute.cardBrand, 'Lowercase:', cardBrand);
    // Check for Amex
    if (cardBrand === 'amex') {
      return {
        formats: 'Supported formats: TIFF, PDF, JPEG, JPG, PNG, DOC, Maximum individual file size: 4MB, Maximum PDF pages: less than 20 pages, Maximum image size: Not more than 300 x 300 DPI',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, DOC, PNG and maximum combined file sizes: 25MB across all files.'
      };
    }
    // Check for Mastercard
    if (cardBrand === 'mastercard') {
      return {
        formats: 'Accepted file formats: TIFF, PDF, JPEG, PNG. File name restrictions: Use both English characters and numbers. Maximum document / image size: Not more than 300 x 300 DPI and pixel count should be less than 30 million',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, PNG and maximum combined file sizes: 14.5MB across all files.'
      };
    }
    // Visa and other card brands (default)
    return {
      formats: 'Supported formats: TIFF, PDF, PNG, JPEG. Maximum individual file size: 2MB. Maximum page size for TIFF and JPEG: 8.64 inches x 14 inches',
      language: 'Evidence language: English',
      files: 'Supported files: TIFF, TIF, PDF, PNG, JPEG, JPG and maximum combined file sizes: 10MB across all files.'
    };
  };
  const shippingAddress = 'Bank Rakyat Tower, Jalan Tangsi, 50732 Kuala Lumpur, Malaysia';
  const billingAddress = 'Bank Rakyat Tower, Jalan Tangsi, 50732 Kuala Lumpur, Malaysia';
  const formatCurrency = (amount: number, currency: string) => {
    return formatCurrencyAmount(amount, {
      currency,
      fractionDigits: 2
    });
  };
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  const simulateFileUpload = (step: 'evidence' | 'order' | 'customer' | 'other') => {
    const mockFile: UploadedFile = {
      id: Math.random().toString(36).substr(2, 9),
      name: 'OHMM-Cairo-Specs-She...9Jul25.pdf',
      size: 1040000,
      uploadedAt: new Date()
    };
    switch (step) {
      case 'evidence':
        setUploadedFiles([...uploadedFiles, mockFile]);
        break;
      case 'order':
        setOrderUploadedFiles([...orderUploadedFiles, mockFile]);
        break;
      case 'customer':
        setCustomerUploadedFiles([...customerUploadedFiles, mockFile]);
        break;
      case 'other':
        setOtherUploadedFiles([...otherUploadedFiles, mockFile]);
        break;
    }
  };
  const removeFile = (fileId: string, step: 'evidence' | 'order' | 'customer' | 'other') => {
    switch (step) {
      case 'evidence':
        setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'order':
        setOrderUploadedFiles(orderUploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'customer':
        setCustomerUploadedFiles(customerUploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'other':
        setOtherUploadedFiles(otherUploadedFiles.filter(f => f.id !== fileId));
        break;
    }
  };
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChallengeReasonNext = () => {
    setChallengeStep('evidence');
  };
  const handleEvidenceNext = () => {
    setChallengeStep('order');
  };
  const handleOrderNext = () => {
    setChallengeStep('customer');
  };
  const handleCustomerNext = () => {
    setChallengeStep('other');
  };
  const handleOtherNext = () => {
    if (noEvidenceChecked && noOrderEvidenceChecked && noCustomerEvidenceChecked && noOtherEvidenceChecked) {
      setShowNoEvidenceWarning(true);
    } else {
      setChallengeStep('review');
    }
  };
  const handleReviewBack = () => {
    setChallengeStep('other');
  };
  const handleSubmit = () => {
    setChallengeStep('submitted');
  };
  const handleClose = () => {
    navigate(`/disputes/${id}`);
  };
  const handleViewAllDisputes = () => {
    navigate('/disputes');
  };
  const handleBackToDisputeDetails = () => {
    navigate(`/disputes/${id}`, {
      state: {
        challenged: true
      }
    });
  };
  return <div className="min-h-screen bg-white" data-id="element-5243">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10" data-id="element-5244">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5245">
          <div className="flex items-center justify-between h-16" data-id="element-5246">
            <h1 className="text-lg font-semibold text-gray-900" data-id="element-5247">
              {t('pages.ChallengeDispute.challenge_dispute')}
            </h1>
            <button onClick={handleClose} aria-label="Close" className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100" data-id="element-5248">
              <XIcon size={20} aria-hidden="true" data-id="element-5249" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-5250">
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-5251">
          {/* Sidebar Navigation */}
          <div className="w-full lg:w-64 flex-shrink-0" data-id="element-5252">
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2 sticky top-24" data-id="element-5253">
              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 text-gray-500" onClick={() => toggleSection('challengeDetails')} data-id="element-5254">
                <span data-id="element-5255">{t('pages.ChallengeDispute.challenge_details')}</span>
                {expandedSections.challengeDetails ? <ChevronUpIcon size={16} className="ml-auto" data-id="element-5256" /> : <ChevronDownIcon size={16} className="ml-auto" data-id="element-5257" />}
              </button>
              {expandedSections.challengeDetails && <div className="ml-3 space-y-1" data-id="element-5258">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'reason' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`} data-id="element-5259">
                    {['evidence', 'order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5260" />}
                    {t('pages.ChallengeDispute.challenge_reason')}
                  </button>
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'evidence' ? 'text-blue-600 bg-blue-50' : challengeStep === 'reason' ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5261">
                    {['order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5262" />}
                    {t('pages.ChallengeDispute.supporting_evidence')}
                  </button>
                </div>}

              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('transactionDetails')} data-id="element-5263">
                <span data-id="element-5264">{t('pages.ChallengeDispute.transaction_details')}</span>
                {expandedSections.transactionDetails ? <ChevronUpIcon size={16} data-id="element-5265" /> : <ChevronDownIcon size={16} data-id="element-5266" />}
              </button>
              {expandedSections.transactionDetails && <div className="ml-3 space-y-1" data-id="element-5267">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'order' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5268">
                    {['customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5269" />}
                    {t('pages.ChallengeDispute.order_details')}
                  </button>
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'customer' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5270">
                    {['other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5271" />}
                    {t('pages.ChallengeDispute.customer_details')}
                  </button>
                </div>}

              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('otherEvidence')} data-id="element-5272">
                <span data-id="element-5273">{t('pages.ChallengeDispute.other_evidence')}</span>
                {expandedSections.otherEvidence ? <ChevronUpIcon size={16} data-id="element-5274" /> : <ChevronDownIcon size={16} data-id="element-5275" />}
              </button>
              {expandedSections.otherEvidence && <div className="ml-3 space-y-1" data-id="element-5276">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'other' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order', 'customer'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5277">
                    {challengeStep === 'review' && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5278" />}
                    {t('pages.ChallengeDispute.other_supporting_documents')}
                  </button>
                </div>}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1" data-id="element-5279">
            {challengeStep === 'reason' && <div className="max-w-3xl" data-id="element-5280">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5281">
                  <div data-id="element-5282">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5283">
                      {t('pages.ChallengeDispute.challenge_reason')}
                    </h1>
                    <p className="text-gray-600" data-id="element-5284">
                      {t('pages.ChallengeDispute.your_responses_will_help_us_recommend')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5285">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5286">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5287">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5288">
                      <div className="text-gray-500" data-id="element-5289">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5290">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5291">
                      <div className="text-gray-500" data-id="element-5292">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5293">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5294">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5295">
                  <div data-id="element-5296">
                    <label className="block text-base font-semibold text-gray-900 mb-1" data-id="element-5297">
                      {t('pages.ChallengeDispute.why_are_you_challenging_the_dispute')}
                    </label>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5298">
                      {t('pages.ChallengeDispute.choose_the_closest_option')}
                    </p>
                    <div className="space-y-3" data-id="element-5299">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5300">
                        <input type="radio" name="challengeReason" value="withdrew" checked={selectedChallengeReason === 'withdrew'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5301" />

                        <span className="text-base text-gray-900" data-id="element-5302">
                          {t('pages.ChallengeDispute.the_customer_withdrew_the_dispute')}
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5303">
                        <input type="radio" name="challengeReason" value="refunded" checked={selectedChallengeReason === 'refunded'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5304" />

                        <span className="text-base text-gray-900" data-id="element-5305">
                          {t('pages.ChallengeDispute.the_customer_has_already_been_refunded')}
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5306">
                        <input type="radio" name="challengeReason" value="purchasing_history" checked={selectedChallengeReason === 'purchasing_history'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5307" />

                        <span className="text-base text-gray-900" data-id="element-5308">
                          {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5309">
                        <input type="radio" name="challengeReason" value="other" checked={selectedChallengeReason === 'other'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5310" />

                        <span className="text-base text-gray-900" data-id="element-5311">
                          {t('pages.ChallengeDispute.other_reasons')}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div data-id="element-5312">
                    <label className="block text-base font-semibold text-gray-900 mb-1" data-id="element-5313">
                      {t('pages.ChallengeDispute.what_type_of_productservice_was_the')}
                    </label>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5314">
                      {t('pages.ChallengeDispute.choose_the_closest_option')}
                    </p>
                    <div className="space-y-3" data-id="element-5315">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5316">
                        <input type="radio" name="productType" value="physical" checked={selectedProductType === 'physical'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5317" />

                        <div className="flex-1" data-id="element-5318">
                          <div className="text-base text-gray-900 font-medium" data-id="element-5319">
                            {t('pages.ChallengeDispute.physical_product')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-5320">
                            {t('pages.ChallengeDispute.physical_goods_to_be_delivered_to')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5321">
                        <input type="radio" name="productType" value="digital" checked={selectedProductType === 'digital'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5322" />

                        <div className="flex-1" data-id="element-5323">
                          <div className="text-base text-gray-900 font-medium" data-id="element-5324">
                            {t('pages.ChallengeDispute.digital_product_or_service')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-5325">
                            {t('pages.ChallengeDispute.purchased_access_to_software_digital_files')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5326">
                        <input type="radio" name="productType" value="offline" checked={selectedProductType === 'offline'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5327" />

                        <div className="flex-1" data-id="element-5328">
                          <div className="text-base text-gray-900 font-medium" data-id="element-5329">
                            {t('pages.ChallengeDispute.offline_service')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-5330">
                            {t('pages.ChallengeDispute.labour_or_assistant_services_provided_either')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5331">
                        <input type="radio" name="productType" value="reservation" checked={selectedProductType === 'reservation'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5332" />

                        <div className="flex-1" data-id="element-5333">
                          <div className="text-base text-gray-900 font-medium" data-id="element-5334">
                            {t('pages.ChallengeDispute.reservation_or_booking')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-5335">
                            {t('pages.ChallengeDispute.services_related_to_reservations_or_bookings')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5336">
                        <input type="radio" name="productType" value="other_product" checked={selectedProductType === 'other_product'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5337" />

                        <span className="text-base text-gray-900" data-id="element-5338">
                          {t('pages.ChallengeDispute.other')}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5339">
                  <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5340">
                    {t('pages.ChallengeDispute.cancel')}
                  </button>
                  <button onClick={handleChallengeReasonNext} disabled={!selectedProductType} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300" data-id="element-5341">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'evidence' && <div className="max-w-3xl" data-id="element-5342">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5343">
                  <div data-id="element-5344">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5345">
                      {evidenceRecs.title}
                    </h1>
                    <p className="text-gray-600" data-id="element-5346">
                      {evidenceRecs.description}
                      {evidenceRecs.learnMoreLink && <>
                          {' '}
                          <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-5347">
                            {evidenceRecs.learnMoreLink}
                          </a>
                        </>}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5348">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5349">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5350">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5351">
                      <div className="text-gray-500" data-id="element-5352">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5353">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5354">
                      <div className="text-gray-500" data-id="element-5355">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5356">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5357">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5358">
                  <div data-id="element-5359">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5360">
                      {t('pages.ChallengeDispute.recommended_evidence')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5361">
                      {t('pages.ChallengeDispute.based_on_your_challenge_reason_we')}
                    </p>
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 mb-4" data-id="element-5362">
                      {evidenceRecs.recommendations.map((rec, index) => <li key={index} data-id="element-5363">
                          <strong data-id="element-5364">{rec.title}</strong>
                          {rec.description && `: ${rec.description}`}
                        </li>)}
                    </ul>
                  </div>

                  <div data-id="element-5365">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5366">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-5367">
                      {(() => {
                    const requirements = getFileFormatRequirements();
                    return <>
                            {requirements.formats}
                            <br data-id="element-5368" />
                            {requirements.language}
                            <br data-id="element-5369" />
                            {requirements.files}
                          </>;
                  })()}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {uploadedFiles.length > 0 && <div className="space-y-3" data-id="element-5370">
                      {uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-5371">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-5372">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5373">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5374" />

                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5375" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-5376">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-5377">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-5378">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-5379">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5380">
                              <EyeIcon size={20} data-id="element-5381" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'evidence')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5382">
                              <Trash2Icon size={20} data-id="element-5383" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-5384">
                    <button disabled={noEvidenceChecked} onClick={() => simulateFileUpload('evidence')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5385">
                      <UploadIcon size={16} data-id="element-5386" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-5387">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5388" />
                  </div>

                  <div data-id="element-5389">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5390">
                      <input type="checkbox" checked={noEvidenceChecked} onChange={e => setNoEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5391" />

                      <span className="text-sm text-gray-900" data-id="element-5392">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5393">
                  <button onClick={() => setChallengeStep('reason')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5394">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleEvidenceNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5395">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'order' && <div className="max-w-3xl" data-id="element-5396">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5397">
                  <div data-id="element-5398">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5399">
                      {t('pages.ChallengeDispute.order_details')}
                    </h1>
                    <p className="text-gray-600" data-id="element-5400">
                      {t('pages.ChallengeDispute.please_provide_as_much_information_as')}
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-5401">
                      {t('pages.ChallengeDispute.the_details_below_have_been_prefilled')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5402">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5403">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5404">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5405">
                      <div className="text-gray-500" data-id="element-5406">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5407">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5408">
                      <div className="text-gray-500" data-id="element-5409">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5410">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5411">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5412">
                  <div data-id="element-5413">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5414">
                      {t('pages.ChallengeDispute.product_details')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5415">
                      <span data-id="element-5416">
                        {t('pages.ChallengeDispute.iphone7_5')}{' '}
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5417">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5418">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5419" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5420">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5421">
                      {t('pages.ChallengeDispute.shipping_address')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5422">
                      <span data-id="element-5423">{shippingAddress}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5424">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5425">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5426" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5427">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5428">
                      {t('pages.ChallengeDispute.shipping_carrier')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5429">
                      <span data-id="element-5430">{t('pages.ChallengeDispute.sameday')}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5431">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5432">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5433" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5434">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5435">
                      {t('pages.ChallengeDispute.provide_additional_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5436">
                      {t('pages.ChallengeDispute.as_the_payment_was_for_a')}{' '}
                      <span className="font-medium" data-id="element-5437">
                        {t('pages.ChallengeDispute.physical_product')}
                      </span>
                      {t('pages.ChallengeDispute.we_recommend_you_to_also_provide')}
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-4" data-id="element-5438">
                      <li data-id="element-5439">{t('pages.ChallengeDispute.shipping_date')}</li>
                      <li data-id="element-5440">
                        {t('pages.ChallengeDispute.shipping_tracking_number')}
                      </li>
                    </ul>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5441">
                      {t('pages.ChallengeDispute.you_can_upload_any_document_containing')}
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5442">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-5443" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-5444" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  <div data-id="element-5445">
                    <button disabled={noOrderEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOrderEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5446">
                      <UploadIcon size={16} data-id="element-5447" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-5448">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noOrderEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOrderEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5449" />
                  </div>

                  <div data-id="element-5450">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5451">
                      <input type="checkbox" checked={noOrderEvidenceChecked} onChange={e => setNoOrderEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5452" />

                      <span className="text-sm text-gray-900" data-id="element-5453">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5454">
                  <button onClick={() => setChallengeStep('evidence')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5455">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleOrderNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5456">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'customer' && <div className="max-w-3xl" data-id="element-5457">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5458">
                  <div data-id="element-5459">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5460">
                      {t('pages.ChallengeDispute.customer_details')}
                    </h1>
                    <p className="text-gray-600" data-id="element-5461">
                      {t('pages.ChallengeDispute.please_provide_as_much_information_as_2')}
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-5462">
                      {t('pages.ChallengeDispute.the_details_below_have_been_prefilled')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5463">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5464">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5465">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5466">
                      <div className="text-gray-500" data-id="element-5467">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5468">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5469">
                      <div className="text-gray-500" data-id="element-5470">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5471">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5472">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5473">
                  <div data-id="element-5474">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5475">
                      {t('pages.ChallengeDispute.customer_name')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5476">
                      <span data-id="element-5477">{t('pages.ChallengeDispute.test_name')}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5478">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5479">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5480" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5481">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5482">
                      {t('pages.ChallengeDispute.customer_email')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5483">
                      <span data-id="element-5484">
                        {t('pages.ChallengeDispute.customerexamplecom')}
                      </span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5485">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5486">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5487" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5488">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5489">
                      {t('pages.ChallengeDispute.customer_purchase_ip')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5490">
                      <span data-id="element-5491">35.221.170.121</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5492">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5493">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5494" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5495">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5496">
                      {t('pages.ChallengeDispute.customer_billing_address')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5497">
                      <span data-id="element-5498">{billingAddress}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5499">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5500">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5501" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5502">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5503">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5504">
                      {t('pages.ChallengeDispute.we_recommend_you_to_provide_the')}
                    </p>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5505">
                      {t('pages.ChallengeDispute.you_can_upload_any_document_containing_2')}
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5506">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-5507" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-5508" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {customerUploadedFiles.length > 0 && <div className="space-y-3" data-id="element-5509">
                      {customerUploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-5510">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-5511">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5512">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5513" />

                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5514" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-5515">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-5516">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-5517">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-5518">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5519">
                              <EyeIcon size={20} data-id="element-5520" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'customer')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5521">
                              <Trash2Icon size={20} data-id="element-5522" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-5523">
                    <button disabled={noCustomerEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noCustomerEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5524">
                      <UploadIcon size={16} data-id="element-5525" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-5526">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noCustomerEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noCustomerEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5527" />
                  </div>

                  <div data-id="element-5528">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5529">
                      <input type="checkbox" checked={noCustomerEvidenceChecked} onChange={e => setNoCustomerEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5530" />

                      <span className="text-sm text-gray-900" data-id="element-5531">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5532">
                  <button onClick={() => setChallengeStep('order')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5533">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleCustomerNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5534">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'other' && <div className="max-w-3xl" data-id="element-5535">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5536">
                  <div data-id="element-5537">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5538">
                      {t('pages.ChallengeDispute.other_supporting_documents')}
                    </h1>
                    <p className="text-gray-600" data-id="element-5539">
                      {t('pages.ChallengeDispute.if_you_have_any_other_documents')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5540">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5541">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5542">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5543">
                      <div className="text-gray-500" data-id="element-5544">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5545">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5546">
                      <div className="text-gray-500" data-id="element-5547">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5548">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5549">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5550">
                  <div data-id="element-5551">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5552">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5553">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-5554" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-5555" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {otherUploadedFiles.length > 0 && <div className="space-y-3" data-id="element-5556">
                      {otherUploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-5557">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-5558">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5559">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5560" />

                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5561" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-5562">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-5563">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-5564">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-5565">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5566">
                              <EyeIcon size={20} data-id="element-5567" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'other')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5568">
                              <Trash2Icon size={20} data-id="element-5569" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-5570">
                    <button disabled={noOtherEvidenceChecked} onClick={() => simulateFileUpload('other')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOtherEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5571">
                      <UploadIcon size={16} data-id="element-5572" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-5573">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noOtherEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOtherEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5574" />
                  </div>

                  <div data-id="element-5575">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5576">
                      <input type="checkbox" checked={noOtherEvidenceChecked} onChange={e => setNoOtherEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5577" />

                      <span className="text-sm text-gray-900" data-id="element-5578">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5579">
                  <button onClick={() => setChallengeStep('customer')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5580">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleOtherNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5581">
                    {t('pages.ChallengeDispute.review')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'review' && <div className="max-w-3xl" data-id="element-5582">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5583">
                  <div data-id="element-5584">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5585">
                      {t('pages.ChallengeDispute.review_your_challenge')}
                    </h1>
                    <p className="text-gray-600" data-id="element-5586">
                      {t('pages.ChallengeDispute.please_confirm_that_the_information_you')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5587">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5588">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5589">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5590">
                      <div className="text-gray-500" data-id="element-5591">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5592">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5593">
                      <div className="text-gray-500" data-id="element-5594">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5595">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5596">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5597">
                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-5598">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5599">
                      {t('pages.ChallengeDispute.challenge_details')}
                    </h3>
                    <div className="space-y-3" data-id="element-5600">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5601">
                        <span className="text-sm text-gray-600" data-id="element-5602">
                          {t('pages.ChallengeDispute.reason_for_challenge')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5603">
                          {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5604">
                        <span className="text-sm text-gray-600" data-id="element-5605">
                          {t('pages.ChallengeDispute.type_of_product_or_service')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5606">
                          {t('pages.ChallengeDispute.physical_product')}
                        </span>
                      </div>
                      {uploadedFiles.length > 0 && <div className="flex flex-col gap-2" data-id="element-5607">
                          <span className="text-sm text-gray-600" data-id="element-5608">
                            {t('pages.ChallengeDispute.supporting_evidence')}
                          </span>
                          <div className="space-y-2" data-id="element-5609">
                            {uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-2 text-sm" data-id="element-5610">
                                <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-5611">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5612">
                                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5613" />

                                    <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5614" />
                                  </svg>
                                </div>
                                <div className="flex-1" data-id="element-5615">
                                  <div className="text-gray-900 font-medium" data-id="element-5616">
                                    {file.name}
                                  </div>
                                  <div className="text-gray-500" data-id="element-5617">
                                    {formatFileSize(file.size)}
                                  </div>
                                </div>
                                <button className="text-blue-600 hover:text-blue-700" data-id="element-5618">
                                  <EyeIcon size={16} data-id="element-5619" />
                                </button>
                              </div>)}
                          </div>
                        </div>}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-5620">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5621">
                      {t('pages.ChallengeDispute.transaction_details')}
                    </h3>
                    <div className="space-y-3" data-id="element-5622">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5623">
                        <span className="text-sm text-gray-600" data-id="element-5624">
                          {t('pages.ChallengeDispute.product_details')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5625">
                          {t('pages.ChallengeDispute.iphone7_5')}{' '}
                          {formatCurrency(dispute.amount, dispute.currency)}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5626">
                        <span className="text-sm text-gray-600" data-id="element-5627">
                          {t('pages.ChallengeDispute.shipping_address')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5628">
                          {shippingAddress}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5629">
                        <span className="text-sm text-gray-600" data-id="element-5630">
                          {t('pages.ChallengeDispute.customer_name')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5631">
                          {t('pages.ChallengeDispute.test_name')}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5632">
                        <span className="text-sm text-gray-600" data-id="element-5633">
                          {t('pages.ChallengeDispute.customer_email')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5634">
                          {t('pages.ChallengeDispute.customerexamplecom')}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5635">
                        <span className="text-sm text-gray-600" data-id="element-5636">
                          {t('pages.ChallengeDispute.customer_purchase_ip')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5637">
                          35.221.170.121
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5638">
                        <span className="text-sm text-gray-600" data-id="element-5639">
                          {t('pages.ChallengeDispute.customer_billing_address')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5640">
                          {billingAddress}
                        </span>
                      </div>
                    </div>
                  </div>

                  {(noEvidenceChecked || noOrderEvidenceChecked || noCustomerEvidenceChecked || noOtherEvidenceChecked) && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-5641">
                      <AlertTriangleIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-5642" />

                      <p className="text-sm text-gray-900" data-id="element-5643">
                        {t('pages.ChallengeDispute.please_attach_at_least_one_of')}
                      </p>
                    </div>}

                  <div data-id="element-5644">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5645">
                      <input type="checkbox" checked={reviewConfirmChecked} onChange={e => setReviewConfirmChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5646" />

                      <span className="text-sm text-gray-900" data-id="element-5647">
                        {t('pages.ChallengeDispute.i_understand_that_the_current_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5648">
                  <button onClick={handleReviewBack} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5649">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleSubmit} disabled={!reviewConfirmChecked} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5650">
                    {t('pages.ChallengeDispute.submit')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'submitted' && <div className="max-w-3xl mx-auto py-12" data-id="element-5651">
                <div className="text-center mb-8" data-id="element-5652">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-6" data-id="element-5653">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5654">
                      <path d="M24 4L6 14L24 24L42 14L24 4Z" fill="#10B981" stroke="#10B981" strokeWidth="2" strokeLinejoin="round" data-id="element-5655" />

                      <path d="M6 24L24 34L42 24" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5656" />

                      <path d="M6 34L24 44L42 34" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5657" />
                    </svg>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-5658">
                    {t('pages.ChallengeDispute.your_dispute_challenge_has_been_submitted')}
                  </h1>
                  <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-5659">
                    {t('pages.ChallengeDispute.thank_you_for_submitting_your_information')}
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-8 space-y-8" data-id="element-5660">
                  <div data-id="element-5661">
                    <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-5662">
                      {t('pages.ChallengeDispute.dispute_challenge')}
                    </h2>

                    <div className="space-y-6" data-id="element-5663">
                      <div data-id="element-5664">
                        <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-5665">
                          {t('pages.ChallengeDispute.challenge_details')}
                        </h3>
                        <div className="space-y-3" data-id="element-5666">
                          <div className="flex" data-id="element-5667">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5668">
                              {t('pages.ChallengeDispute.reason_for_challenge')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5669">
                              {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5670">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5671">
                              {t('pages.ChallengeDispute.type_of_product_or_service')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5672">
                              {t('pages.ChallengeDispute.physical_product')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5673">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5674">
                              {t('pages.ChallengeDispute.supporting_evidence')}
                            </span>
                            <div className="flex-1" data-id="element-5675">
                              {uploadedFiles.length > 0 ? uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 mb-2" data-id="element-5676">
                                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-5677">
                                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5678">
                                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5679" />

                                        <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5680" />
                                      </svg>
                                    </div>
                                    <div className="flex-1" data-id="element-5681">
                                      <div className="text-sm font-medium text-gray-900" data-id="element-5682">
                                        {file.name}
                                      </div>
                                      <div className="text-sm text-gray-500" data-id="element-5683">
                                        {formatFileSize(file.size)}
                                      </div>
                                    </div>
                                    <button className="text-blue-600 hover:text-blue-700" data-id="element-5684">
                                      <EyeIcon size={18} data-id="element-5685" />
                                    </button>
                                  </div>) : <span className="text-sm text-gray-900" data-id="element-5686">
                                  {t('pages.ChallengeDispute.no_files_uploaded')}
                                </span>}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div data-id="element-5687">
                        <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-5688">
                          {t('pages.ChallengeDispute.transaction_details')}
                        </h3>
                        <div className="space-y-3" data-id="element-5689">
                          <div className="flex" data-id="element-5690">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5691">
                              {t('pages.ChallengeDispute.product_details')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5692">
                              {t('pages.ChallengeDispute.iphone7_5')}{' '}
                              {formatCurrency(dispute.amount, dispute.currency)}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5693">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5694">
                              {t('pages.ChallengeDispute.shipping_address')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5695">
                              {shippingAddress}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5696">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5697">
                              {t('pages.ChallengeDispute.customer_name')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5698">
                              {t('pages.ChallengeDispute.test_name')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5699">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5700">
                              {t('pages.ChallengeDispute.customer_email')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5701">
                              {t('pages.ChallengeDispute.customerexamplecom')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5702">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5703">
                              {t('pages.ChallengeDispute.customer_purchase_ip')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5704">
                              35.221.170.121
                            </span>
                          </div>
                          <div className="flex" data-id="element-5705">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5706">
                              {t('pages.ChallengeDispute.customer_billing_address')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5707">
                              {billingAddress}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3" data-id="element-5708">
                    <button onClick={handleViewAllDisputes} className="px-6 py-3 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 rounded-md hover:bg-blue-50" data-id="element-5709">
                      {t('pages.ChallengeDispute.view_all_disputes')}
                    </button>
                    <button onClick={handleBackToDisputeDetails} className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-5710">
                      {t('pages.ChallengeDispute.back_to_dispute_details')}
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* No Evidence Warning Modal */}
      {showNoEvidenceWarning && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5711">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-5712">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowNoEvidenceWarning(false)} data-id="element-5713">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-5714"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-5715">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-5716">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-5717">
                <div className="flex items-start gap-4" data-id="element-5718">
                  <div className="flex-shrink-0" data-id="element-5719">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-5720">
                      <AlertTriangleIcon size={24} className="text-red-600" data-id="element-5721" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-5722">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-5723">
                      {t('pages.ChallengeDispute.are_you_sure_you_want_to')}
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-5724">
                      {t('pages.ChallengeDispute.submitting_a_challenge_without_supporting_evidence')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3" data-id="element-5725">
                <button onClick={() => setShowNoEvidenceWarning(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 order-2 sm:order-1" data-id="element-5726">
                  {t('pages.ChallengeDispute.go_back_and_add_evidence')}
                </button>
                <button onClick={() => {
              setShowNoEvidenceWarning(false);
              setChallengeStep('review');
            }} className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 order-1 sm:order-2" data-id="element-5727">
                  {t('pages.ChallengeDispute.proceed_anyway')}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}